import { useState, useEffect } from 'react';
import { Button } from './Button';
import { useToast } from '../hooks/useToast';
import { useLanguage } from '../hooks/useLanguage';

const getAvailableFields = (t) => [
  { key: 'full_name', label: t('fullNameLabel'), alwaysShow: true },
  { key: 'phone', label: t('phoneLabel') },
  { key: 'message', label: t('messageLabel') },
  { key: 'action_date', label: t('actionDate') },
  { key: 'ad_name', label: t('adName') },
  { key: 'campaign_name', label: t('campaignName') },
  { key: 'adset_name', label: t('adsetName') },
  { key: 'form_id', label: t('formId') },
  { key: 'ad_id', label: t('adId') },
  { key: 'campaign_id', label: t('campaignId') },
  { key: 'fb_lead_id', label: t('fbLeadId') },
  { key: 'created_time', label: t('createdTime') },
  { key: 'created_at', label: t('createdDate'), alwaysShow: true },
  { key: 'assigned_to', label: t('assignedToLabel') },
  { key: 'source', label: t('sourceLabel') },
];

const DEFAULT_CARD_FIELDS = ['full_name', 'phone', 'action_date', 'created_at', 'assigned_to'];
const DEFAULT_MODAL_FIELDS = ['full_name', 'phone', 'message', 'ad_name', 'campaign_name', 'created_at', 'assigned_to', 'source'];
const DEFAULT_TAGS = ['Hot Lead', 'Follow Up', 'Qualified', 'Not Interested', 'Callback Requested'];

export const ViewSettingsManager = () => {
  const [cardFields, setCardFields] = useState([]);
  const [modalFields, setModalFields] = useState([]);
  const [activityTags, setActivityTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const toast = useToast();
  const { t } = useLanguage();
  const AVAILABLE_FIELDS = getAvailableFields(t);

  useEffect(() => {
    // Load settings from localStorage
    const savedCardFields = localStorage.getItem('kanban_card_fields');
    const savedModalFields = localStorage.getItem('kanban_modal_fields');
    const savedTags = localStorage.getItem('activity_tags');

    setCardFields(savedCardFields ? JSON.parse(savedCardFields) : DEFAULT_CARD_FIELDS);
    setModalFields(savedModalFields ? JSON.parse(savedModalFields) : DEFAULT_MODAL_FIELDS);
    setActivityTags(savedTags ? JSON.parse(savedTags) : DEFAULT_TAGS);
  }, []);

  const handleSaveCardFields = () => {
    localStorage.setItem('kanban_card_fields', JSON.stringify(cardFields));
    toast.success(t('cardDisplaySettingsSaved'));
  };

  const handleSaveModalFields = () => {
    localStorage.setItem('kanban_modal_fields', JSON.stringify(modalFields));
    toast.success(t('modalDisplaySettingsSaved'));
  };

  const handleSaveTags = () => {
    localStorage.setItem('activity_tags', JSON.stringify(activityTags));
    toast.success(t('activityTagsSaved'));
  };

  const toggleCardField = (fieldKey) => {
    const field = AVAILABLE_FIELDS.find(f => f.key === fieldKey);
    if (field?.alwaysShow) return;

    setCardFields(prev =>
      prev.includes(fieldKey)
        ? prev.filter(f => f !== fieldKey)
        : [...prev, fieldKey]
    );
  };

  const toggleModalField = (fieldKey) => {
    const field = AVAILABLE_FIELDS.find(f => f.key === fieldKey);
    if (field?.alwaysShow) return;

    setModalFields(prev =>
      prev.includes(fieldKey)
        ? prev.filter(f => f !== fieldKey)
        : [...prev, fieldKey]
    );
  };

  const addTag = () => {
    if (!newTag.trim()) return;
    if (activityTags.includes(newTag.trim())) {
      toast.error(t('tagAlreadyExists'));
      return;
    }
    setActivityTags([...activityTags, newTag.trim()]);
    setNewTag('');
  };

  const removeTag = (tag) => {
    setActivityTags(activityTags.filter(t => t !== tag));
  };

  const resetToDefaults = () => {
    setCardFields(DEFAULT_CARD_FIELDS);
    setModalFields(DEFAULT_MODAL_FIELDS);
    setActivityTags(DEFAULT_TAGS);
    localStorage.setItem('kanban_card_fields', JSON.stringify(DEFAULT_CARD_FIELDS));
    localStorage.setItem('kanban_modal_fields', JSON.stringify(DEFAULT_MODAL_FIELDS));
    localStorage.setItem('activity_tags', JSON.stringify(DEFAULT_TAGS));
    toast.success(t('resetToDefaultSettings'));
  };

  return (
    <div className="space-y-8">
      {/* Card Fields */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('kanbanCardFields')}</h3>
            <p className="text-sm text-gray-500 mt-1">{t('selectCardFields')}</p>
          </div>
          <Button variant="primary" size="sm" onClick={handleSaveCardFields}>
            {t('saveCardSettings')}
          </Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {AVAILABLE_FIELDS.map((field) => (
            <label
              key={field.key}
              className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 ${
                cardFields.includes(field.key) ? 'bg-blue-50 border-blue-500' : 'border-gray-200'
              } ${field.alwaysShow ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <input
                type="checkbox"
                checked={cardFields.includes(field.key)}
                onChange={() => toggleCardField(field.key)}
                disabled={field.alwaysShow}
                className="rounded text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">
                {field.label}
                {field.alwaysShow && <span className="text-xs text-gray-400 ml-1">({t('required')})</span>}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Modal Fields */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('modalViewFields')}</h3>
            <p className="text-sm text-gray-500 mt-1">{t('selectModalFields')}</p>
          </div>
          <Button variant="primary" size="sm" onClick={handleSaveModalFields}>
            {t('saveModalSettings')}
          </Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {AVAILABLE_FIELDS.map((field) => (
            <label
              key={field.key}
              className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 ${
                modalFields.includes(field.key) ? 'bg-blue-50 border-blue-500' : 'border-gray-200'
              } ${field.alwaysShow ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <input
                type="checkbox"
                checked={modalFields.includes(field.key)}
                onChange={() => toggleModalField(field.key)}
                disabled={field.alwaysShow}
                className="rounded text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">
                {field.label}
                {field.alwaysShow && <span className="text-xs text-gray-400 ml-1">({t('required')})</span>}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Activity Tags */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('activityTags')}</h3>
            <p className="text-sm text-gray-500 mt-1">{t('manageTags')}</p>
          </div>
          <Button variant="primary" size="sm" onClick={handleSaveTags}>
            {t('saveTags')}
          </Button>
        </div>

        <div className="mb-4">
          <div className="flex gap-2">
            <input
              type="text"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addTag()}
              placeholder={t('addNewTag')}
              className="flex-1 rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
            <Button variant="primary" onClick={addTag}>
              {t('addTag')}
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {activityTags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
            >
              {tag}
              <button
                onClick={() => removeTag(tag)}
                className="text-blue-600 hover:text-blue-800"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      </div>

      {/* Reset Button */}
      <div className="flex justify-end">
        <Button variant="secondary" onClick={resetToDefaults}>
          {t('resetToDefaults')}
        </Button>
      </div>
    </div>
  );
};
